import React from 'react';
import { Link } from 'react-router-dom';

import { Footer } from './components';
import { ROUTE } from 'src/constants/routes';

import './legal.scss';

const CONTACT_EMAIL = 'ruleenginebot@gmail.com';
const EFFECTIVE_DATE = 'August 12, 2026';

export const Privacy = () => {
  return (
    <div className="legal-page">
      <main className="legal-page__main">
        <div className="legal-page__container">
          <h1 className="legal-page__title">Privacy Policy</h1>
          <p className="legal-page__meta">
            Effective date: <strong>{EFFECTIVE_DATE}</strong>
          </p>

          <section className="legal-page__section">
            <h2>1. Introduction</h2>
            <p>
              <strong>RulesEngineBot</strong> ("we", "us", "our") is committed to protecting your
              personal information. This Privacy Policy explains what data we collect, how we use
              it, and your rights regarding that data when you use our Service across web pages,
              dashboard tools, embed pages, and desktop integrations.
            </p>
            <p>
              By using the Service, you agree to the collection and use of information in accordance
              with this policy.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>2. Data We Collect</h2>

            <h3>2.1 Twitch Account Data</h3>
            <p>
              When you connect your Twitch account via OAuth, we receive and store the following
              data provided by Twitch:
            </p>
            <ul>
              <li>Twitch user ID and display name</li>
              <li>Twitch channel name and broadcaster type</li>
              <li>Profile image URL</li>
              <li>OAuth access token (stored securely, used to interact with Twitch API)</li>
              <li>OAuth refresh token where required for persistent API access</li>
            </ul>

            <h3>2.2 Account Access and Team Data</h3>
            <p>
              For accounts using role-based dashboard access, we may store access-management data
              such as:
            </p>
            <ul>
              <li>Team member login and display name</li>
              <li>Assigned role and active/inactive status</li>
              <li>Platform and page permission assignments</li>
            </ul>

            <h3>2.3 Viewer Data</h3>
            <p>
              For viewers who interact with bot commands in a streamer's chat, we may collect and
              store:
            </p>
            <ul>
              <li>Twitch username and user ID</li>
              <li>Watch time (duration of presence in channel while the stream is live)</li>
              <li>Whether the viewer is a follower of the channel</li>
              <li>Giveaway participation records</li>
              <li>Command interaction history (e.g., ticket claim records)</li>
              <li>Chaos Mode interaction records related to Channel Points-triggered actions</li>
            </ul>

            <h3>2.4 Dashboard and Configuration Data</h3>
            <p>We store streamer-defined configuration needed to run the Service, including:</p>
            <ul>
              <li>Command rules and response templates</li>
              <li>Automode settings and schedule intervals</li>
              <li>Giveaway settings, ticket commands, winners, and reroll history</li>
              <li>Chaos Mode action settings, cooldowns, and reward mappings</li>
              <li>Giveaway Embed and Chaos Widget publishing settings</li>
            </ul>

            <h3>2.5 Usage and Technical Data</h3>
            <p>
              We may automatically collect technical information such as IP addresses, browser type,
              operating system, and pages visited, used for service operation, abuse prevention, and
              security monitoring.
            </p>

            <h3>2.6 Desktop Application Data</h3>
            <p>
              If you use the desktop application, local app settings and session-related values may
              be stored on your device to support login state and bot tooling. We do not
              intentionally collect files from your computer beyond what is required for declared
              app functionality.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>3. How We Use Your Data</h2>
            <p>We use the collected data to:</p>
            <ul>
              <li>Authenticate you and maintain your account session</li>
              <li>Operate bot commands, giveaways, and watch-time tracking in Twitch chat</li>
              <li>Operate Chaos Mode reward-to-action flows and cooldown controls</li>
              <li>Enforce role-based access to platforms and dashboard pages</li>
              <li>Display analytics and viewer data within your dashboard</li>
              <li>Ensure the security and integrity of the Service</li>
              <li>Respond to support requests and communications</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p>
              We do not use your data for advertising purposes, and we do not sell your personal
              data to third parties.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>4. Data Sharing</h2>
            <p>We may share your data only in the following limited circumstances:</p>
            <ul>
              <li>
                <strong>Twitch:</strong> Authentication and live data are fetched via Twitch's Helix
                API under your OAuth authorization.
              </li>
              <li>
                <strong>Infrastructure providers:</strong> We may use hosting, database, and
                delivery providers acting on our instructions to operate the Service.
              </li>
              <li>
                <strong>Legal requirements:</strong> We may disclose data if required by law, court
                order, or governmental authority.
              </li>
            </ul>
            <p>
              We do not sell personal data. Third-party access is limited to service operation,
              security, and legal compliance.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>5. Data Retention</h2>
            <p>
              We retain your account data for as long as your account is active or as needed to
              provide the Service. Viewer and interaction data (including watch time, giveaway
              entries, and Chaos interaction events) may be retained while your workspace remains
              active, unless you request deletion where applicable.
            </p>
            <p>
              If you disconnect your Twitch account or request account deletion, we will remove or
              anonymize personal data within a reasonable period, except where retention is required
              by law or necessary for fraud/security investigations.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>6. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul>
              <li>
                <strong>Access</strong> — request a copy of the personal data we hold about you
              </li>
              <li>
                <strong>Rectification</strong> — request correction of inaccurate data
              </li>
              <li>
                <strong>Erasure</strong> — request deletion of your personal data ("right to be
                forgotten")
              </li>
              <li>
                <strong>Portability</strong> — request your data in a structured, machine-readable
                format
              </li>
              <li>
                <strong>Objection</strong> — object to certain processing of your data
              </li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="legal-page__link">
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </section>

          <section className="legal-page__section">
            <h2>7. Cookies and Local Storage</h2>
            <p>
              We use browser <strong>localStorage</strong> to store your language preference and
              authentication token. We may also store minimal UI state to keep dashboard behavior
              consistent.
            </p>
            <p>
              We do not use third-party advertising cookies. You can clear browser storage at any
              time through your browser privacy settings.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>8. Data Security</h2>
            <p>
              We implement industry-standard security measures including HTTPS encryption, secure
              token storage, permission-based access controls, and operational monitoring to protect
              your data. However, no method of transmission over the Internet or electronic storage
              is 100% secure.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>9. Payments and Billing Data</h2>
            <p>
              In-app payments are currently not enabled during the controlled testing phase. If
              billing is introduced later, this policy will be updated to describe payment data
              handling and providers before paid plans become active.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>10. Children's Privacy</h2>
            <p>
              The Service is not directed to individuals under the age of 13 (or the applicable age
              of digital consent in your country). We do not knowingly collect personal data from
              children. If you believe a child has provided us data, please contact us and we will
              delete it promptly.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>11. International Data Handling</h2>
            <p>
              Depending on your location, your data may be processed in countries different from
              your own. Where applicable, we apply reasonable safeguards for cross-border data
              handling consistent with legal requirements.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on this
              page with an updated effective date. We encourage you to review this policy
              periodically. Continued use of the Service after changes constitutes your acceptance
              of the revised policy.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>13. Contact Us</h2>
            <p>
              If you have any questions, concerns, or data requests regarding this Privacy Policy,
              please contact us at:
            </p>
            <p>
              <a href={`mailto:${CONTACT_EMAIL}`} className="legal-page__link">
                {CONTACT_EMAIL}
              </a>
            </p>
          </section>

          <div className="legal-page__footer-nav">
            <Link to={ROUTE.TERMS} className="legal-page__link">
              Terms of Service →
            </Link>
            <Link to={ROUTE.MAIN} className="legal-page__link">
              ← Back to Home
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
